import './PaneItemsContainer'

import get from 'lodash/get'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { AIMS } from '@disco/constants/FeatureTypes'
import { CUSTOM_CHANNEL } from '@disco/constants/GroupTypes'
import { SEARCH_RESULTS_PANE } from '@disco/constants/PaneTypes'
import { RANGE_SELECTION } from '@disco/constants/SelectionTypes'
import { selectExplorerItems } from '@disco/state-management/actions/SelectionActions'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import type { SelectableItem } from '@disco/types/types/selection'
import { getBrowseSearchDragData } from '@disco/utils/mixpanel/drop-tracks'

import { SelectionContext } from 'contexts/selection'

import BriefItem from 'components/explorer/brief-item'
import ExplorerBriefProject from 'components/explorer/brief-project'
import ExplorerTrackItem from 'components/explorer/track-item'
import PaneUploadItem from 'components/explorer/pane/pane-items/pane-upload-item'
import InfiniteScroll from 'components/ui/infinite-scroll'
import ManualScroll from 'components/ui/manual-scroll'
import Sortable from 'components/ui/sortable'

import { useDispatch } from 'hooks/use-redux'

import ExplorerArtist from '../../artist'
import ExplorerPlaylist from '../../playlist'
import ExplorerSection from '../explorer-section'
import { LoadMore } from '../load-more'
import LoadingMore from '../loading-more'
import GroupItem from './group-item'
import type { PaneItemsProps } from './PaneItemsContainer'

const getSection = (pane, section) => {
  const id = `pane-${pane.index}-section-${section.id}`
  return <ExplorerSection id={id} key={id} section={section} />
}

const keyExtractor = (item: Record<string, any>, index: number): string =>
  `${item.id}-${index}`

export type Props = PaneItemsProps
const COMPACT_DISPLAY_THRESHOLD = 5

const PaneItems = (props: PaneItemsProps): React.ReactNode => {
  const {
    manualScroll,
    items,
    loadNext,
    withSortable,
    subPaneOpen,
    nextPageUrl,
  } = props

  const dispatch = useDispatch()
  const selectionContext = useContext(SelectionContext)

  const deselectedActiveItemsRef = useRef<Set<number>>(new Set())
  const prevActiveIndexRef = useRef<number | null>(null)
  const prevSelectionRef = useRef<{
    selection: number[]
    activeIndex: number | null
  }>({ selection: [], activeIndex: null })

  const [compactDisplayDismissed, setCompactDisplayDismissed] = useState(false)

  const aimsEnabled = useFeatureEnabled(AIMS)
  const { type: paneType, searchQueryV2, groupId } = props.pane
  const searchDragData = useMemo(
    () =>
      // A search results pane with a groupId shows a channel, not a search.
      paneType === SEARCH_RESULTS_PANE &&
      typeof groupId !== 'number' &&
      searchQueryV2
        ? getBrowseSearchDragData(searchQueryV2, aimsEnabled)
        : undefined,
    [paneType, groupId, searchQueryV2, aimsEnabled],
  )

  const activeItemIndex = useMemo(() => {
    const raw = props.pane?.activeItem
    if (!raw) return null
    const i = parseInt(raw.split('/')[1] ?? '', 10)
    return isNaN(i) ? null : i
  }, [props.pane?.activeItem])

  // Effect 1 must stay declared before Effect 2. It reads prevSelectionRef.current.selection
  // to detect user deselection; Effect 2 writes that field in the same render cycle. React
  // guarantees effects run in declaration order, so this read-before-write is correct only
  // while that order is preserved.
  useEffect(() => {
    if (activeItemIndex === null) return

    const prevActiveIndex = prevActiveIndexRef.current
    const isNewActiveItem = prevActiveIndex !== activeItemIndex
    prevActiveIndexRef.current = activeItemIndex

    if (isNewActiveItem) {
      // Clear the old item's flag so navigating back to it starts fresh.
      if (prevActiveIndex !== null)
        deselectedActiveItemsRef.current.delete(prevActiveIndex)
      deselectedActiveItemsRef.current.delete(activeItemIndex)
      return
    }
    if (
      props.paneSelection?.length &&
      !props.paneSelection.includes(activeItemIndex) &&
      prevSelectionRef.current.selection.includes(activeItemIndex) &&
      !deselectedActiveItemsRef.current.has(activeItemIndex)
    ) {
      deselectedActiveItemsRef.current.add(activeItemIndex)
    }
  }, [activeItemIndex, props.paneSelection])

  useEffect(() => {
    if (activeItemIndex === null) return
    if (!props.paneSelection?.length) {
      prevSelectionRef.current = { selection: [], activeIndex: activeItemIndex }
      // Session ended — clear all stale deselect flags so a new session starts fresh.
      deselectedActiveItemsRef.current.clear()
      return
    }
    if (prevSelectionRef.current.activeIndex !== activeItemIndex) return
    if (deselectedActiveItemsRef.current.has(activeItemIndex)) return

    if (props.paneSelection.includes(activeItemIndex)) {
      prevSelectionRef.current = {
        selection: props.paneSelection,
        activeIndex: activeItemIndex,
      }
      return
    }

    const selection = [...props.paneSelection, activeItemIndex]
    const sorted = [...selection].sort((a, b) => a - b)
    const prevSorted = [...prevSelectionRef.current.selection].sort(
      (a, b) => a - b,
    )

    const isSame =
      sorted.length === prevSorted.length &&
      sorted.every((idx, i) => idx === prevSorted[i])

    if (isSame) return
    prevSelectionRef.current = {
      selection,
      activeIndex: activeItemIndex,
    }
    dispatch(
      selectExplorerItems(selectionContext.id, selection, RANGE_SELECTION),
    )
  }, [activeItemIndex, dispatch, props.paneSelection, selectionContext.id])

  const applySectionsSplitting = paneItems => {
    const result = [...paneItems]
    const { sections } = props

    if (sections && sections.length > 0) {
      for (let i = result.length - 1; i >= 0; i--) {
        const sectionsToInsert = sections.filter(s => s.position === i)

        if (sectionsToInsert.length > 0) {
          result.splice(
            i,
            0,
            sectionsToInsert.map(section => {
              return getSection(props.pane, section)
            }),
          )
        }
      }

      const appendSections = sections.filter(
        s => s.position === props.items.length,
      )
      result.push(appendSections.map(s => getSection(props.pane, s)))
    }

    return result
  }

  const getPaneAppearance = (
    index: number,
  ): 'selected' | 'active' | 'default' => {
    const { paneSelection, pane, itemPrefix = 'pane' } = props

    if (paneSelection && paneSelection.indexOf(index) > -1) {
      return 'selected'
    } else if (pane.activeItem == `${itemPrefix}/${index}`) {
      return 'active'
    } else {
      return 'default'
    }
  }

  const getItemBlock = (item, index) => {
    if (!item) return null
    const { itemPrefix } = props
    let itemBlock: React.ReactElement | null = null
    let sourceGroupId: number | null = null
    let sourceGroupType: string | null = null

    if (props.pane.groupType === CUSTOM_CHANNEL) {
      sourceGroupId = get(props.pane, 'groupId', null)
      sourceGroupType = get(props.pane, 'groupType', null)
    } else {
      sourceGroupId = get(props, 'library.id', null)
      sourceGroupType = get(props, 'library.group_type', null)
    }

    const appearance = getPaneAppearance(index)

    switch (item.type) {
      case 'artist':
        itemBlock = (
          <ExplorerArtist
            key={item.id}
            itemPrefix={itemPrefix}
            appearance={appearance}
            artist={item}
            index={index}
            pane={props.pane}
          />
        )
        break

      case 'group':
        itemBlock = (
          <GroupItem
            key={`group-${item.id}`}
            groupId={item.id}
            index={index}
            appearance={appearance}
            pane={props.pane}
          />
        )
        break

      case 'upload':
        itemBlock = (
          <PaneUploadItem
            key={`upload-${item.tempId}`}
            tempId={item.tempId}
            pane={props.pane}
          />
        )
        break

      case 'track':
        itemBlock = (
          <ExplorerTrackItem
            key={`track-${item.id}-${index}`}
            appearance={appearance}
            disableClickHandling={props.disableItemClick}
            disableSelection={props.disableSelection}
            disabledDrop={props.disabledDrop}
            disablePaneOpen={props.disablePaneOpen}
            index={index}
            itemPrefix={itemPrefix}
            isSimilarity={props.isSimilarity}
            pane={props.pane}
            sourcePlaylistId={props.pane.playlist_id}
            track={item}
            versionsButton={props.trackVersionsButton}
            sourceGroupId={sourceGroupId}
            sourceGroupType={sourceGroupType}
            searchDragData={searchDragData}
          />
        )
        break
      case 'brief':
        itemBlock = (
          <BriefItem
            key={`brief-${item.id}-${index}`}
            brief={item}
            appearance={appearance}
            index={index}
            pane={props.pane}
            itemPrefix={itemPrefix}
            isSearchResult={!!props.pane.searchTerm}
          />
        )
        break

      case 'brief_project':
        itemBlock = (
          <ExplorerBriefProject
            key={`project-${item.id}-${index}`}
            project={item}
            appearance={appearance}
            index={index}
            pane={props.pane}
            selectedItems={props.selectedItems as SelectableItem[]}
          />
        )
        break

      default:
        itemBlock = (
          <ExplorerPlaylist
            key={index}
            itemPrefix={itemPrefix}
            appearance={appearance}
            groups={props.groups}
            index={index}
            library={props.library}
            pane={props.pane}
            playlist={item}
            sourceGroupId={sourceGroupId}
            sourceGroupType={sourceGroupType}
            searchDragData={searchDragData}
          />
        )
    }

    return itemBlock
  }

  // we need filter to prevent items to be rendered while it's not available
  // in reducers
  // TODO: don't create paneItems in render method
  const filteredItems = items.filter(Boolean)
  let paneItems = filteredItems.map(getItemBlock)
  paneItems = applySectionsSplitting(paneItems)
  const canCompactDisplay =
    manualScroll && subPaneOpen && paneItems.length > COMPACT_DISPLAY_THRESHOLD
  const compactDisplay = canCompactDisplay && !compactDisplayDismissed

  if (compactDisplay && paneItems.length > COMPACT_DISPLAY_THRESHOLD) {
    paneItems = paneItems.slice(0, COMPACT_DISPLAY_THRESHOLD)
  }

  const nextPage = useCallback(() => {
    if (!compactDisplay) loadNext()
    setCompactDisplayDismissed(true)
  }, [loadNext, compactDisplay])

  if (withSortable) {
    return (
      <Sortable
        keyExtractor={keyExtractor}
        accept={withSortable.accept}
        id={withSortable.id}
        items={filteredItems}
        renderItem={getItemBlock}
      />
    )
  }

  const component = manualScroll ? ManualScroll : InfiniteScroll
  let next = nextPageUrl || null
  if (compactDisplay) next = 'fullPage'
  return React.createElement<any>(
    component,
    {
      next,
      loadNext: nextPage,
      loadingNode: <LoadingMore />,
      loadMoreNode: manualScroll ? <LoadMore /> : undefined,
    },
    paneItems,
  )
}

export default PaneItems
